import React, { useEffect, useRef, useState } from 'react';
import { adminService } from '../../services/adminService';
import { meetbotService } from '../../services/meetbotService';
import { FALLBACK_MEETING_PLATFORMS } from '../../lib/recognition/routeLink';

/**
 * Площадки бота на встречах: тумблер на каждую, настройка meetbot_platforms
 * в bit-auth.
 *
 * Бот перечитывает настройку раз в минуту (bit-meetbot/app/platform_settings.py),
 * поэтому правка действует без перевыката. Выключенная площадка отвечает
 * человеку «не понимаю ссылку», а идущие встречи не прерываются.
 *
 * 🔴 В настройку уходит ТОЛЬКО площадка, которую админ нажал, поверх уже
 * сохранённого словаря. Площадка, которой в настройке нет, живёт по флагу
 * окружения бота (KTALK_ENABLED и т.д.), и её тумблер показывает то, что бот
 * делает сейчас. До правки по ревью 30.09 первое же нажатие записывало весь
 * словарь — и показанные умолчания (или запасное «всё включено») навсегда
 * перекрывали флаги окружения, в том числе выключенные.
 *
 * Новая площадка — строка здесь. `inDevelopment` — адаптера ещё нет: тумблер
 * виден, но не нажимается. `note` — строка-оговорка под названием.
 *
 * Zoom включается как все с 30.09.2026: адаптер есть (bit-meetbot/app/platforms/zoom.py),
 * по умолчанию выключен. Вход гостем в браузере Zoom запрещает правилами —
 * решение владельца включать его всё равно, поэтому оговорка видна админу.
 */
export const MEETBOT_PLATFORMS = [
  { name: 'telemost', title: 'Телемост' },
  { name: 'ktalk', title: 'Контур.Толк' },
  { name: 'bitlink', title: 'БИТ.Link' },
  { name: 'linkgo', title: 'БИТ.LinkGo' },
  { name: 'zoom', title: 'Zoom', note: 'вход гостем в браузере — против правил Zoom' },
];

const ACTIVE = MEETBOT_PLATFORMS.filter((p) => !p.inDevelopment);

/**
 * Что показать на тумблере, пока площадка в настройке не названа.
 *
 * Не названа — бот живёт по своему флагу окружения, и узнать его можно
 * только у бота (GET /platforms отдаёт включённые). Бот не ответил — берём
 * запасной список экрана: он совпадает с умолчаниями прода.
 */
function initialState(value, effectiveNames) {
  const fallback = new Set(FALLBACK_MEETING_PLATFORMS.map((p) => p.name));
  const state = {};
  ACTIVE.forEach(({ name }) => {
    if (value && typeof value[name] === 'boolean') state[name] = value[name];
    else if (effectiveNames) state[name] = effectiveNames.has(name);
    else state[name] = fallback.has(name);
  });
  return state;
}

// Что сказать админу, если бот работает не по сохранённой настройке.
const SOURCE_NOTES = {
  env: 'Бот не прочитал настройку и работает по умолчаниям сервера — сохранённое пока не действует.',
  stale: 'Бот не смог перечитать настройку и работает по последней прочитанной — свежие изменения могут не действовать.',
};

/**
 * Args:
 *   value: сохранённое meetbot_platforms из /admin/settings (null/нет — не задано).
 *   loaded: настройки загрузились. 🔴 Не загрузились — тумблеры не нажимаются:
 *     PATCH заменяет словарь целиком, и нажатие поверх неизвестного
 *     сохранённого значения стёрло бы все остальные площадки.
 */
const MeetbotPlatforms = ({ value, loaded = true, onError }) => {
  const [enabled, setEnabled] = useState(() => initialState(value, null));
  const [botSilent, setBotSilent] = useState(false);
  const [source, setSource] = useState(null);
  // Сохранённый словарь: к нему добавляется только нажатая площадка.
  const stored = useRef(value && typeof value === 'object' ? { ...value } : {});
  // Админ успел нажать до ответа бота — ответ его выбор не перетирает.
  const touched = useRef(false);
  const allNamed = !!value && ACTIVE.every(({ name }) => typeof value[name] === 'boolean');

  // Настройки пришли заново (перезагрузка, сохранение формы выше) — сохранённый
  // словарь берётся из них, иначе следующее нажатие отправило бы устаревший.
  const firstValue = useRef(true);
  useEffect(() => {
    if (firstValue.current) { firstValue.current = false; return; }
    stored.current = value && typeof value === 'object' ? { ...value } : {};
    if (value && typeof value === 'object') {
      setEnabled((prev) => {
        const next = { ...prev };
        ACTIVE.forEach(({ name }) => {
          if (typeof value[name] === 'boolean') next[name] = value[name];
        });
        return next;
      });
    }
  }, [value]);

  useEffect(() => {
    // Бота спрашиваем всегда: даже когда всё названо, админке нужно знать,
    // прочитал ли он настройку (source).
    let alive = true;
    Promise.resolve()
      .then(() => meetbotService.getPlatformsState())
      .then((data) => {
        if (!alive) return;
        const list = data?.platforms;
        if (!Array.isArray(list)) throw new Error('неожиданный ответ /platforms');
        setSource(typeof data.source === 'string' ? data.source : null);
        if (!touched.current) setEnabled(initialState(value, new Set(list.map((p) => p?.name))));
      })
      .catch(() => { if (alive) setBotSilent(true); });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (name) => {
    if (!loaded) return;
    touched.current = true;
    const before = enabled;
    const storedBefore = stored.current;
    const nextValue = !enabled[name];
    const nextStored = { ...stored.current, [name]: nextValue };
    stored.current = nextStored;
    setEnabled((prev) => ({ ...prev, [name]: nextValue }));
    try {
      await adminService.updateSettings({ meetbot_platforms: nextStored });
      onError?.(null);  // прежняя ошибка к удачному сохранению не относится
    } catch (e) {
      stored.current = storedBefore;
      setEnabled(before);
      onError?.(e?.response?.data?.detail || 'Не удалось изменить площадки бота');
    }
  };

  return (
    <>
      <p className="text-xs text-paper-hint" data-testid="meetbot-platforms-hint">
        Сохраняется сразу; бот применит изменение в течение минуты. Идущие встречи не прерываются.
        {botSilent && !allNamed && ' Бот не ответил — для не менявшихся площадок показаны умолчания.'}
      </p>
      {!loaded && (
        <p className="text-xs text-paper-accent" data-testid="meetbot-platforms-not-loaded">
          Настройки не загрузились — площадки менять нельзя, чтобы не затереть сохранённое.
          Обновите страницу.
        </p>
      )}
      {SOURCE_NOTES[source] && (
        <p className="text-xs text-paper-accent" data-testid="meetbot-platforms-source">
          {SOURCE_NOTES[source]}
        </p>
      )}
      {MEETBOT_PLATFORMS.map(({ name, title, inDevelopment, note }) => {
        const on = !inDevelopment && !!enabled[name];
        return (
          <div key={name} className="flex justify-between items-start">
            <div>
              <div className="text-sm font-medium text-paper-ink-2">{title}</div>
              {inDevelopment && <div className="text-xs text-paper-hint">адаптер в разработке</div>}
              {note && <div className="text-xs text-paper-hint">{note}</div>}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label={title}
              data-testid={`toggle-meetbot-${name}`}
              disabled={inDevelopment || !loaded}
              onClick={() => toggle(name)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                on ? 'bg-paper-success' : 'bg-paper-track'
              } ${inDevelopment || !loaded ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-paper-card transition-transform ${
                on ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        );
      })}
    </>
  );
};

export default MeetbotPlatforms;
